import { useCallback, useRef } from 'react'

/** localStorage is ~5MB shared; skip persisting entries larger than this (memory cache still applies). */
const MAX_LOCALSTORAGE_SERIALIZED_BYTES = 12_000

type CacheEntry<T = unknown> = {
  data: T
  timestamp: number
}

type CacheUtils<T = unknown> = {
  getCached: (id: string | number | null | undefined) => T | null
  setCached: (id: string | number | null | undefined, data: T) => void
  invalidate: (id: string | number | null | undefined) => void
  invalidateAll: () => void
  clearExpired: () => void
}

/**
 * Internal generic cache hook for managing cached data
 * Supports both memory and localStorage caching with expiration
 */
export const useCache = <T = unknown>(
  cacheKeyPrefix: string,
  expiryMs: number = 5 * 60 * 1000,
): CacheUtils<T> => {
  // In-memory cache (Map) - persists during component lifecycle
  const memoryCacheRef = useRef(new Map<string, CacheEntry<T>>())

  /**
   * Get cache key for a given ID
   */
  const getCacheKey = useCallback(
    (id: string | number | null | undefined): string => {
      // Handle null/undefined IDs
      const idStr = id === null || id === undefined ? 'null' : String(id)
      return `${cacheKeyPrefix}${idStr}`
    },
    [cacheKeyPrefix],
  )

  /**
   * Get cached data
   */
  const getCached = useCallback(
    (id: string | number | null | undefined): T | null => {
      const cacheKey = getCacheKey(id)
      const now = Date.now()

      // Check memory cache first
      const memoryEntry = memoryCacheRef.current.get(cacheKey)
      if (memoryEntry) {
        const { data, timestamp } = memoryEntry
        if (now - timestamp < expiryMs) {
          return data
        }
        memoryCacheRef.current.delete(cacheKey)
      }

      // Check localStorage
      try {
        const stored = localStorage.getItem(cacheKey)
        if (stored) {
          const parsed: CacheEntry<T> = JSON.parse(stored)
          const { data, timestamp } = parsed
          if (now - timestamp < expiryMs) {
            memoryCacheRef.current.set(cacheKey, { data, timestamp })
            return data
          }
          localStorage.removeItem(cacheKey)
        }
      } catch (error) {
        console.error(`Error reading cache for ${cacheKey}:`, error)
        try {
          localStorage.removeItem(cacheKey)
        } catch {
          console.error(`Error removing cache for ${cacheKey}:`, error)
        }
      }

      return null
    },
    [getCacheKey, expiryMs],
  )

  /**
   * Set cached data
   */
  const setCached = useCallback(
    (id: string | number | null | undefined, data: T): void => {
      const cacheKey = getCacheKey(id)
      const timestamp = Date.now()
      const entry: CacheEntry<T> = { data, timestamp }

      // Update memory cache
      memoryCacheRef.current.set(cacheKey, entry)

      const serialized = JSON.stringify(entry)
      if (serialized.length > MAX_LOCALSTORAGE_SERIALIZED_BYTES) {
        // TiTiler colormap JSON (256×RGBA) and similar payloads: keep session memory only
        return
      }

      // Update localStorage
      try {
        localStorage.setItem(cacheKey, serialized)
      } catch (error) {
        const isQuota =
          error instanceof DOMException && error.name === 'QuotaExceededError'
        if (!isQuota) {
          console.error(`Error writing cache for ${cacheKey}:`, error)
        }
        try {
          clearExpired()
          localStorage.setItem(cacheKey, JSON.stringify(entry))
        } catch (retryError: unknown) {
          const quotaRetry =
            retryError instanceof DOMException &&
            retryError.name === 'QuotaExceededError'
          if (quotaRetry) {
            // Expected when storage stays full: entry remains in memoryCacheRef only.
            return
          }
          console.warn(
            `[useCache] localStorage write failed after clearExpired for key ${cacheKey}:`,
            retryError,
          )
        }
      }
    },
    [getCacheKey],
  )

  /**
   * Invalidate cache for a specific ID
   */
  const invalidate = useCallback(
    (id: string | number | null | undefined): void => {
      const cacheKey = getCacheKey(id)

      memoryCacheRef.current.delete(cacheKey)

      try {
        localStorage.removeItem(cacheKey)
      } catch (error) {
        console.error(`Error invalidating cache for ${cacheKey}:`, error)
      }
    },
    [getCacheKey],
  )

  /**
   * Invalidate all cache entries with this prefix
   */
  const invalidateAll = useCallback((): void => {
    memoryCacheRef.current.clear()

    try {
      const keysToRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(cacheKeyPrefix)) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key))
    } catch (error) {
      console.error(`Error clearing cache for prefix ${cacheKeyPrefix}:`, error)
    }
  }, [cacheKeyPrefix])

  /**
   * Clear expired cache entries
   */
  const clearExpired = useCallback((): void => {
    const now = Date.now()

    for (const [key, entry] of memoryCacheRef.current.entries()) {
      if (now - entry.timestamp >= expiryMs) {
        memoryCacheRef.current.delete(key)
      }
    }

    // Clear expired localStorage entries
    try {
      const keysToRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(cacheKeyPrefix)) {
          try {
            const stored = localStorage.getItem(key)
            if (stored) {
              const parsed: CacheEntry<T> = JSON.parse(stored)
              const { timestamp } = parsed
              if (now - timestamp >= expiryMs) {
                keysToRemove.push(key)
              }
            }
          } catch {
            // Invalid entry, remove it
            keysToRemove.push(key)
          }
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key))
    } catch (error) {
      console.error(
        `Error clearing expired cache for prefix ${cacheKeyPrefix}:`,
        error,
      )
    }
  }, [cacheKeyPrefix, expiryMs])

  return {
    getCached,
    setCached,
    invalidate,
    invalidateAll,
    clearExpired,
  }
}
